import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { FaPlus } from "react-icons/fa";
import Spinner from 'react-bootstrap/Spinner';
import { API } from 'aws-amplify';
import './TaskBoard.css';
import '../../../components/PageHeader/PageHeader.css';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { useI18n } from '../../../i18n';
import Board, { createTranslate } from 'react-trello';
import BoardCard from './BoardCard/BoardCard';
import BoardList from './BoardList/BoardList';
import NewCard from './NewCard/NewCard';
import NewLane from './NewLane/NewLane';
import LaneHeader from './LaneHeader/LaneHeader';
import NewLaneSection from './NewLaneSection/NewLaneSection';
import AddCardLink from './AddCardLink/AddCardLink';
import BoardLookup from './BoardLookup';
import { useToasts } from 'react-toast-notifications';

function refKey(value) {
    return String(value || '').replace(/^#/, '').trim().toLowerCase();
}

const apiHeaders = {
    'x-api-key': process.env.REACT_APP_API_KEY,
    'Accept': 'application/json',
    'Content-Type': 'application/json',
};

const TaskBoard = (props) => {
	const { t } = useI18n();
	const [board, setBoard] = useState();
	const [boardData, setBoardData] = useState({lanes: []});
    const [isLoading, setIsLoading] = useState(true);
    const [view, setView] = useState('board');
    const [tasksById, setTasksById] = useState({});
    const [tasksByRef, setTasksByRef] = useState({});
    const [assetsById, setAssetsById] = useState({});
    const [assetsByRef, setAssetsByRef] = useState({});
    const { addToast } = useToasts();
    const latestData = useRef({ lanes: [] });
    const boardRef = useRef(board);
    boardRef.current = board;

    const params = { headers: apiHeaders };

    useEffect(()=>{
        const fetchBoard = async () => {
            setIsLoading(true);
            try {
                const boardId = "default";
                const loaded = await API.get('board','/' + boardId, params);
                const safeBoard = (loaded && typeof loaded === 'object') ? loaded : { boardId };
                let data = { lanes: [] };
                if (safeBoard.data && safeBoard.data.lanes){
                    data = {
                        ...safeBoard.data,
                        lanes: safeBoard.data.lanes.map((lane) => ({
                            ...lane,
                            cards: Array.isArray(lane.cards) ? lane.cards : [],
                        })),
                    };
                }
                boardRef.current = safeBoard;
                setBoard(safeBoard);
                latestData.current = data;
                setBoardData(data);
            } catch (error) {
                console.log(error);
                latestData.current = { lanes: [] };
                setBoardData({ lanes: [] });
            } finally {
                setIsLoading(false);
            }
        }
        fetchBoard();

        const fetchLookups = async () => {
            try {
                const [tasks, assets] = await Promise.all([
                    API.get('task_active_all', '/', params),
                    API.get('assets', '/', params),
                ]);
                const nextTasks = {};
                const nextTasksByRef = {};
                (tasks || []).forEach((item) => {
                    if (item && item.taskId) nextTasks[item.taskId] = item;
                    if (item && item.task_ref) nextTasksByRef[refKey(item.task_ref)] = item;
                });
                const nextAssets = {};
                const nextAssetsByRef = {};
                (assets || []).forEach((item) => {
                    if (item && item.assetId) nextAssets[item.assetId] = item;
                    if (item && item.site_ref) nextAssetsByRef[item.site_ref] = item;
                });
                setTasksById(nextTasks);
                setTasksByRef(nextTasksByRef);
                setAssetsById(nextAssets);
                setAssetsByRef(nextAssetsByRef);
            } catch (error) {
                console.log(error);
            }
        };
        fetchLookups();
        }, []);

    const persistBoard = useCallback((data) => {
        const prev = (boardRef.current && typeof boardRef.current === 'object')
            ? boardRef.current
            : { boardId: 'default' };
        const currentBoard = { ...prev, data };
        boardRef.current = currentBoard;
        setBoard(currentBoard);
        API.post('board', '/', { body: currentBoard, headers: apiHeaders })
            .catch((error) => {
                console.log(error);
                addToast(t('toast.errorSavingBoard'), { appearance: 'error' });
            });
    }, [addToast, t]);

    const applyBoardData = useCallback((data) => {
        latestData.current = data;
        setBoardData(data);
        persistBoard(data);
    }, [persistBoard]);

    const handleDataChange = useCallback((data) => {
        latestData.current = data;
        persistBoard(data);
    }, [persistBoard]);

    const addCardToLane = useCallback((laneId, card) => {
        const current = latestData.current || { lanes: [] };
        const next = {
            lanes: (current.lanes || []).map((lane) => (
                lane.id === laneId
                    ? { ...lane, cards: [...(lane.cards || []), { ...card, laneId }] }
                    : lane
            )),
        };
        applyBoardData(next);
    }, [applyBoardData]);

    const addLaneToBoard = useCallback((lane) => {
        const current = latestData.current || { lanes: [] };
        applyBoardData({
            lanes: [...(current.lanes || []), { ...lane, cards: lane.cards || [] }],
        });
        requestAnimationFrame(() => {
            const scroller = document.querySelector('.TaskBoard .react-trello-board > *:first-child');
            if (scroller) scroller.scrollLeft = scroller.scrollWidth;
        });
    }, [applyBoardData]);

    const moveCardToLane = useCallback((cardId, toLaneId) => {
        const current = latestData.current || { lanes: [] };
        let moving = null;
        const stripped = (current.lanes || []).map((lane) => {
            const cards = lane.cards || [];
            const found = cards.find((card) => card.id === cardId);
            if (found) moving = found;
            return found ? { ...lane, cards: cards.filter((card) => card.id !== cardId) } : lane;
        });
        if (!moving) return;
        applyBoardData({
            lanes: stripped.map((lane) => (
                lane.id === toLaneId
                    ? { ...lane, cards: [...(lane.cards || []), { ...moving, laneId: toLaneId }] }
                    : lane
            )),
        });
    }, [applyBoardData]);

    const NewCardForm = useCallback((formProps) => (
        <NewCard
            {...formProps}
            onAdd={(card) => {
                addCardToLane(formProps.laneId, card);
                if (formProps.onCancel) formProps.onCancel();
            }}
        />
    ), [addCardToLane]);

    const NewLaneForm = useCallback((formProps) => (
        <NewLane
            {...formProps}
            onAdd={(lane) => {
                addLaneToBoard(lane);
                if (formProps.onCancel) formProps.onCancel();
            }}
        />
    ), [addLaneToBoard]);

    const lookup = useMemo(
        () => ({
            tasksById,
            tasksByRef,
            assetsById,
            assetsByRef,
            userFullname: props.userFullname || '',
            taskList: Object.keys(tasksById).map((id) => tasksById[id]),
            lanes: (boardData && boardData.lanes) || [],
            moveCard: moveCardToLane,
        }),
        [tasksById, tasksByRef, assetsById, assetsByRef, props.userFullname, boardData, moveCardToLane]
    );

    const components = useMemo(() => ({
        NewCardForm,
        NewLaneForm,
        NewLaneSection,
        AddCardLink,
        Card: BoardCard,
        LaneHeader,
    }), [NewCardForm, NewLaneForm]);

    const BoardText = {
        "Add another lane": <span><FaPlus /> {t('board.addList')}</span>,
        "Click to add card": <span className="board-add-card"><FaPlus /> {t('board.addCard')}</span>,
        "Delete lane": t('board.archiveList'),
        "Lane actions": t('costsUi.actions'),
        "button": {
          "Add lane": t('board.addLane'),
          "Add card": t('board.addCard'),
          "Cancel": t('common.cancel')
        },
        "placeholder": {
          "title": t('common.details'),
          "description": t('common.description'),
          "label": t('common.type')
        }
      }

    const switchView = (next) => {
        if (next === 'list') setBoardData(latestData.current || boardData);
        setView(next);
    };

    const handleListCardChange = (laneId, cardId, patch) => {
        const current = latestData.current || boardData;
        applyBoardData({
            ...current,
            lanes: (current.lanes || []).map((lane) => (
                lane.id === laneId
                    ? {
                        ...lane,
                        cards: (lane.cards || []).map((card) => (
                            card.id === cardId ? { ...card, ...patch } : card
                        )),
                    }
                    : lane
            )),
        });
    };

    const handleListCardDelete = (laneId, cardId) => {
        const current = latestData.current || boardData;
        applyBoardData({
            ...current,
            lanes: (current.lanes || []).map((lane) => (
                lane.id === laneId
                    ? { ...lane, cards: (lane.cards || []).filter((card) => card.id !== cardId) }
                    : lane
            )),
        });
    };

    const cardCount = ((latestData.current && latestData.current.lanes) || boardData.lanes || []).reduce(
        (sum, lane) => sum + ((lane.cards && lane.cards.length) || 0),
        0
    );

return (
    <div className="TaskBoard">
        <PageHeader
            title={t('pages.taskBoard.title')}
            breadcrumbs={[
                { label: t('nav.tasks'), path: '/tasks' },
                { label: t('pages.taskBoard.crumb') },
            ]}
            subtitle={t('pages.taskBoard.subtitle')}
            count={!isLoading ? cardCount : undefined}
            countLabel={t('common.cards')}
            countSingular={t('common.card')}
            actions={
                <div className="board-view-toggle" role="tablist" aria-label="Board view">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={view === 'board'}
                        className={view === 'board' ? 'is-active' : ''}
                        onClick={() => switchView('board')}
                    >
                        Board
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={view === 'list'}
                        className={view === 'list' ? 'is-active' : ''}
                        onClick={() => switchView('list')}
                    >
                        List
                    </button>
                </div>
            }
        />
        <BoardLookup.Provider value={lookup}>
        <div className="task-board-canvas" aria-busy={isLoading}>
            {isLoading ? (
                <div className="loading-container">
                    <div className="loading-content">
                        <Spinner animation="border" role="status" variant="primary" className="loading-spinner" />
                        <div className="loading-text">Loading board...</div>
                    </div>
                </div>
            ) : view === 'list' ? (
                <BoardList
                    data={boardData}
                    onCardChange={handleListCardChange}
                    onCardDelete={handleListCardDelete}
                />
            ) : (
                <Board
                    data={boardData}
                    editable={true}
                    draggable={true}
                    onDataChange={handleDataChange}
                    canAddLanes={true}
                    editLaneTitle={false}
                    hideCardDeleteIcon={true}
                    components={components}
                    t={createTranslate(BoardText)}
                    style={{ height: '100%', overflowX: 'auto', overflowY: 'hidden', alignItems: 'stretch' }}
                    laneStyle={{ minWidth: 220, maxWidth: 280 }}
                    cardStyle={{ padding: 0, marginBottom: '3mm', maxWidth: '100%', minWidth: 0, width: '100%' }}
                />
            )}
        </div>
        </BoardLookup.Provider>
    </div>
);
}

const mapStateToProps = (state) => ({
    userFullname: state && state.auth ? state.auth.userFullname : '',
});

export default connect(mapStateToProps)(TaskBoard);
